/* ica-events.js — ICA behavioral event firing
   Loaded with `defer` in theme.liquid <head> via the homepage layout.

   The 17 events (taxonomy 15 → 17 with Resources Hub + TDS Library + Case Studies):
     1.  tds_downloaded
     2.  sds_downloaded
     3.  catalog_downloaded
     4.  quote_requested
     5.  quote_submitted
     6.  sample_requested
     7.  brand_viewed
     8.  industry_viewed
     9.  distributor_application_submitted
     10. newsletter_subscribed
     11. quote_basket_add            — Add-to-Quote button click (PDP, card, search)
     12. quote_basket_remove         — Drawer line item remove
     13. quote_basket_view           — Drawer open (badge, toast_link, deep_link)
     14. quote_basket_submitted      — Drawer Submit → routes to contact page
                                       (distinct from `quote_submitted` which fires
                                        on the final contact-page form submission)
     15. quote_form_expanded         — r5 Fix #4. Talk-to-Engineer optional-fields
                                       accordion opened. High-intent signal (buyer
                                       willing to share more context). Payload:
                                       { source: 'contact_form', expanded_at: <ISO> }
                                       No counter increment (same as 11-14, per
                                       Decision #11).
     16. tds_library_viewed          — TDS library page (/pages/tds) load + every
                                       filter/sort/search change. Payload:
                                       { source_trigger: 'page_load' |
                                                         'filter_change' |
                                                         'sort_change' |
                                                         'search',
                                         active_filters: { brand: [],
                                                           product_line: [],
                                                           industry: [] },
                                         search_query: <string|null>,
                                         result_count: <int> }
                                       No counter increment.
     17. case_study_viewed           — Individual case study page load. Payload:
                                       { case_study_handle, client_name,
                                         industry_handle, products_referenced: [],
                                         source: 'direct' | 'pdp_cross_link' |
                                                 'hub' | 'search' }
                                       Source resolved from ?from= param or referrer.
                                       No counter increment.
     18. inspection_quote_requested  — T1 #7b. Inspection Services page CTA →
                                       Talk-to-Engineer form submitted with
                                       ?intent=inspection_quote. Payload:
                                       { intent: 'inspection_quote',
                                         inspection_scope: <pre|in|post|failure|spec|other>,
                                         company, country, contact_name,
                                         contact_email, project_description,
                                         page_referrer, timestamp, reference }
                                       Fires from ica-quote-form.js wireSubmit
                                       branch when intent === 'inspection_quote'.
                                       No counter increment at launch (parallel
                                       to Quote-Cart events per Decision #11).

   BRDG receives a PII-free behavioral projection through its installed SDK.
   Transactional quote/contact data uses the separately consent-gated
   /apps/brdg/api/v1/submissions runtime and never crosses this event path.

   Quote-Cart events DO NOT increment customer counter metafields at launch
   (Decision #11). The existing `quote_requested` and `quotes_submitted_count`
   remain authoritative for funnel reporting; the 4 new events feed BRDG
   lifecycle flows directly from the customer_event metaobject log.
*/
(function () {
  'use strict';

  // Counter map: event_type -> customer metafield key to increment.
  var COUNTER_MAP = {
    'tds_downloaded': 'tds_downloads_count',
    'quote_requested': 'quotes_requested_count',
    'quote_submitted': 'quotes_submitted_count',
    'sample_requested': 'samples_requested_count'
  };

  var BRAND_COUNTER_MAP = {
    'polyval-coatings': 'brand_views_polyval',
    'nofire-technologies': 'brand_views_nofire',
    'beyond-powder-coatings': 'brand_views_beyond',
    'heggel': 'brand_views_heggel',
    'polyflex': 'line_views_polyflex',
    'polyur': 'line_views_polyur',
    'xymax': 'line_views_xymax',
    'polyrock': 'line_views_polyrock'
  };

  // T1 #9 B2 — Map BRDG event_type to friendly Klaviyo event names.
  // Unmapped events still track under their raw event_type. Identify fires
  // whenever payload.contact_email is present (form submissions). Read-only
  // page-view events (brand_viewed / industry_viewed / *_library_viewed)
  // have no email so they skip identify and only track.
  var KLAVIYO_EVENT_NAMES = {
    'tds_downloaded': 'TDS Downloaded',
    'sds_downloaded': 'SDS Downloaded',
    'catalog_downloaded': 'Catalog Downloaded',
    'quote_requested': 'Quote Requested',
    'quote_submitted': 'Quote Submitted',
    'sample_requested': 'Sample Requested',
    'distributor_application_submitted': 'Distributor Application Submitted',
    'newsletter_subscribed': 'Newsletter Subscribed',
    'quote_basket_submitted': 'Quote Cart Submitted',
    'inspection_quote_requested': 'Inspection Quote Requested',
    'brand_viewed': 'Brand Viewed',
    'industry_viewed': 'Industry Viewed',
    'case_study_viewed': 'Case Study Viewed',
    // T1 #8 (P10) — WhatsApp Lead Qualifier. Submitted carries the full
    // qualifier payload + reference ID and a contact_email/contact_phone, so
    // the identify branch above creates/updates the Klaviyo profile BEFORE the
    // WhatsApp tab opens (lead captured even if the user never reaches WhatsApp).
    // Abandoned carries only the partial fields the user provided. Neither
    // increments a customer counter metafield (parallel to Quote-Cart events,
    // Decision #11) — they feed BRDG lifecycle flows from the event log + the
    // Klaviyo profile directly.
    'quote_qualifier_submitted': 'Quote Qualifier Submitted',
    'quote_qualifier_abandoned': 'Quote Qualifier Abandoned'
  };

  var INDUSTRY_COUNTER_MAP = {
    'mining': 'industry_views_mining',
    'oil-and-gas': 'industry_views_oilgas',
    'oil-gas': 'industry_views_oilgas',
    'marine-offshore': 'industry_views_marine',
    'refinery-petrochemical': 'industry_views_refinery',
    'power-data-centres': 'industry_views_power',
    'power-data': 'industry_views_power',
    'bridges-tanks-steel': 'industry_views_bridges',
    'roads-civil-construction': 'industry_views_roads',
    'roads-civil': 'industry_views_roads',
    'commercial-industrial-buildings': 'industry_views_commercial',
    'commercial-industrial': 'industry_views_commercial',
    'manufacturing-heavy-equipment': 'industry_views_manufacturing'
  };

  function sessionId() {
    try {
      var sid = sessionStorage.getItem('ica_sid');
      if (!sid) {
        sid = (window.crypto && crypto.randomUUID)
          ? crypto.randomUUID()
          : (Date.now() + '-' + Math.random().toString(36).slice(2));
        sessionStorage.setItem('ica_sid', sid);
      }
      return sid;
    } catch (e) {
      return Date.now() + '-' + Math.random().toString(36).slice(2);
    }
  }

  function determineCounter(eventType, payload) {
    if (COUNTER_MAP[eventType]) return COUNTER_MAP[eventType];
    if (eventType === 'brand_viewed' && payload && payload.brand_handle) {
      return BRAND_COUNTER_MAP[payload.brand_handle] || null;
    }
    if (eventType === 'industry_viewed' && payload && payload.industry_handle) {
      return INDUSTRY_COUNTER_MAP[payload.industry_handle] || null;
    }
    return null;
  }

  function brdgSafePayload(payload) {
    var blocked = /email|phone|name|company|address|city|zip|postal|description|notes|referrer/i;
    var output = {};
    Object.keys(payload || {}).forEach(function (key) {
      var value = payload[key];
      if (blocked.test(key)) return;
      if (value === null || typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
        output[key] = value;
      }
    });
    output.source = 'ica_theme';
    return output;
  }

  window.icaEvents = {
    fire: function (eventType, payload) {
      payload = payload || {};
      var event = {
        event_type: eventType,
        customer_id: window.icaCustomerId || null,
        session_id: sessionId(),
        fired_at: new Date().toISOString(),
        page_url: window.location.href,
        referrer: document.referrer,
        payload: payload,
        event_value: payload.est_value || null,
        product_ref: payload.product_gid || null,
        collection_ref: payload.collection_gid || null
      };

      // 1. Send behavioral telemetry through the installed BRDG SDK. The
      // normal event channel is intentionally PII-free; contact/request data
      // belongs exclusively to the governed submission endpoint.
      if (window.__BRDG__ && typeof window.__BRDG__.track === 'function') {
        try { window.__BRDG__.track(eventType, brdgSafePayload(payload)); } catch (e) { /* silent */ }
      }

      // 2. Echo to GA4 if present.
      if (window.gtag) {
        try { window.gtag('event', eventType, brdgSafePayload(payload)); } catch (e) { /* silent */ }
      }

      // 3. Echo to Klaviyo. T1 #9 B2 — identify-on-email + friendly event names.
      //    Wrapped fail-silent: a missing SDK, blocked CDN, or thrown error
      //    in `_learnq` must NEVER prevent the form from submitting to Shopify.
      if (window._learnq) {
        try {
          var carriesContact = !!(payload && (payload.contact_email || payload.contact_phone || payload.phone));
          var marketingAllowed = payload && payload.marketing_consent === true;
          if (payload && payload.contact_email && marketingAllowed) {
            var firstName, lastName;
            if (payload.contact_name) {
              var parts = String(payload.contact_name).trim().split(/\s+/);
              firstName = parts[0] || undefined;
              lastName = parts.length > 1 ? parts.slice(1).join(' ') : undefined;
            }
            var identity = { $email: payload.contact_email };
            if (firstName) identity.$first_name = firstName;
            if (lastName) identity.$last_name = lastName;
            if (payload.company) identity.company = payload.company;
            if (payload.role) identity.role = payload.role;

            // P9 (F) — Shipping address + regional routing traits.
            // Shopify-reserved Klaviyo location keys ($country/$region/$city/
            // $address1/$zip) feed Klaviyo's native location profile; the
            // market_* keys feed regional-routing/segmentation flows. Each is
            // only attached when the corresponding payload field is present so
            // partial forms never clobber an existing profile value.
            if (payload.ship_country) identity.$country = payload.ship_country;
            else if (payload.country) identity.$country = payload.country;
            if (payload.ship_region) identity.$region = payload.ship_region;
            if (payload.ship_city) identity.$city = payload.ship_city;
            if (payload.ship_address1) identity.$address1 = payload.ship_address1;
            if (payload.ship_zip) identity.$zip = payload.ship_zip;
            if (payload.market) identity.market = payload.market;
            if (payload.market_team_entity) identity.market_team_entity = payload.market_team_entity;
            if (payload.market_team_email) identity.market_team_email = payload.market_team_email;

            window._learnq.push(['identify', identity]);
          }
          if (!carriesContact || marketingAllowed) {
            var klaviyoName = KLAVIYO_EVENT_NAMES[eventType] || eventType;
            window._learnq.push(['track', klaviyoName, carriesContact ? payload : brdgSafePayload(payload)]);
          }
        } catch (e) { /* silent — do not break form submission */ }
      }

      // 4. Dispatch a CustomEvent for QA and downstream theme JS to hook into.
      try {
        window.dispatchEvent(new CustomEvent('icaEventFired', { detail: event }));
      } catch (e) { /* silent */ }
    }
  };
})();
